<script lang="ts">
    import { getContext } from 'svelte'
    import { derived as sderived } from 'svelte/store'
    import type { Readable } from 'svelte/store'

    import Icon from '@iconify/svelte'
    import { orderBy } from 'lodash-es'

    import { gettext as _ } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import { simpleFetch } from 'hydria/services/session'
    import { sanchor, spath } from 'hydria/svelte_helpers'

    import type { DelegateData, DelegateList, DelegateMapping, User, UserHandler } from './user'

    import LoadingPattern from 'hydria/components/LoadingPattern.svelte'

    const userh = getContext('User') as UserHandler

    const status = sderived(userh.status, ($status) => $status)
    const loading = sderived(userh.loading, ($loading) => $loading)

    const user = sderived(userh.userWritable, ($v) => $v) as Readable<User>
    const ddata = sderived(userh.delegateData, ($v) => $v) as Readable<DelegateData>

    interface Delegate {
        id: string
        email: string
        name: string
        org: string
        technical: boolean
        renewal: boolean
    }

    function setupDelegData(dlist: DelegateList, mapping: DelegateMapping): Delegate[] {
        const map: { [key: string]: Delegate } = {}
        for (const type of ['technical', 'renewal']) {
            for (const id of dlist[type]) {
                if (map[id] == null) {
                    if (mapping[id] == null) {
                        console.error('Delegate mapping is missing data for id', id)
                        map[id] = {
                            id: id,
                            email: '–',
                            name: id,
                            // @ts-expect-error
                            org: null,
                            technical: false,
                            renewal: false,
                        }
                    } else {
                        map[id] = { ...mapping[id], id: id, technical: false, renewal: false }
                    }
                }
                map[id][type] = true
            }
        }

        return orderBy(Object.values(map), [(o) => o.email], ['asc'])
    }
    let delegates = $derived($ddata && $ddata.user_delegates && $ddata.user_delegates_mapping ? setupDelegData($ddata.user_delegates, $ddata.user_delegates_mapping) : null)
    let delegateOf = $derived($ddata && $ddata.user_delegate_of && $ddata.user_delegate_of_mapping ? setupDelegData($ddata.user_delegate_of, $ddata.user_delegate_of_mapping) : null)

    type Direction = 'direct' | 'reverse'
    type DType = 'technical' | 'renewal'

    let preRemoveDir: Direction | null = $state(null)
    let preRemoveType: DType | null = $state(null)
    let preRemoveId: string | null = $state(null)

    let newDelegateText: string = $state('')

    function clearPreRemove() {
        preRemoveDir = null
        preRemoveType = null
        preRemoveId = null
    }
    function setPreRemove(dir: string, dtype: string, id: string) {
        preRemoveDir = dir as Direction
        preRemoveType = dtype as DType
        preRemoveId = id
    }

    let working = $state(false)

    function loadDelegateResponse(data: any) {
        Object.assign(userh.raw, {
            user_delegates: data.user_delegates,
            user_delegates_mapping: data.user_delegates_mapping,
            user_delegate_of: data.user_delegate_of,
            user_delegate_of_mapping: data.user_delegate_of_mapping,
        })
        userh.loadDelegateData()
    }

    async function removeDelegate() {
        working = true

        try {
            const r = await simpleFetch('DELETE', `/json/wdom-user/1/${$user.id}/delegate/${preRemoveType}/${preRemoveId}` + (preRemoveDir == 'reverse' ? '/reversed' : ''))
            preRemoveDir = null
            preRemoveType = null
            preRemoveId = null
            notify({
                type: 'success',
                title: 'Délégation',
                text: 'Un délégué a été supprimé.',
            })
            loadDelegateResponse(r.data)
        } catch (e: any) {
            console.error('Delegate delete failure:', e)
            notify({
                type: 'danger',
                title: 'Délégation',
                text: e && e.data && e.data.error ? e.data.error : 'Une erreur est survenue lors de la suppression d’un délégué.',
            })
        }

        working = false
    }

    async function addDelegate(dir: Direction, dtype: DType) {
        working = true

        try {
            const r = await simpleFetch('PUT', `/json/wdom-user/1/${$user.id}/delegate/${dtype}/${newDelegateText}` + (dir == 'reverse' ? '/reversed' : ''))
            newDelegateText = ''
            notify({
                type: 'success',
                title: 'Délégation',
                text: 'Le délégué a été ajouté.',
            })
            loadDelegateResponse(r.data)
        } catch (e: any) {
            console.error('Delegate add failure:', e)
            notify({
                type: 'danger',
                title: 'Délégation',
                text: e && e.data && e.data.error ? e.data.error : 'Une erreur est survenue lors de l’ajout du délégué.',
            })
        }

        working = false
    }

    let delegCats = $derived.by(() => {
        let res = [{ dir: 'direct', title: 'Délégués', list: delegates }]
        if (!$user.has_subaccounts) {
            res.push({ dir: 'reverse', title: 'Délégués de', list: delegateOf })
        }
        return res
    })
</script>

<div class="card">
    <div class="card-title text-primary">{$_('Delegation')}</div>
    {#if $status == 'loading' || $loading}
        <div class="card-body">
            <LoadingPattern pattern={[['flex', ['-:1 0 0', 't:0 0 50px', '-:1 0 0']], 'x', 'x', '-', ['flex', ['-:1 0 0', 't:0 0 50px', '-:1 0 0']], 'x', 'x', '-', '-', ['flex', ['b:1 0 0', 'b:0 0 108px', 'b:0 0 108px']]]} />
        </div>
    {:else if $user && $user.is_subaccount}
        <div class="card-body">Ce compte est un sous-compte.</div>
    {:else}
        <div class="card-body">
            <div class="delegates-grid">
                {#each delegCats as { dir, title, list }}
                    <div class="deleg-title">{title}</div>
                    <div class="deleg-table">
                        <div class="deleg-table-row head">
                            <div>Nom</div>
                            <div>Email</div>
                            <div><span>Tech</span><span></span></div>
                            <div><span>Renew</span><span></span></div>
                        </div>
                        {#if list && list.length}
                            {#each list as item}
                                <div class="deleg-table-row">
                                    <div>
                                        <a use:sanchor href={spath(`/wdom-user/${item.id}`)}>
                                            {#if item.name && item.org}{item.name} ({item.org})
                                            {:else if item.org}? ({item.org})
                                            {:else if item.name}{item.name}
                                            {:else}{item.id}
                                            {/if}
                                        </a>
                                    </div>
                                    <div><span title={item.email}>{item.email}</span></div>
                                    {#each ['technical', 'renewal'] as dtype}
                                        <div>
                                            {#if item[dtype]}
                                                <span><Icon inline icon="fa:check" /></span>
                                            {/if}
                                            <div>
                                                {#if item[dtype]}
                                                    <div class="btn-group btn-group-sm">
                                                        {#if preRemoveDir == dir && preRemoveType == dtype && preRemoveId == item.id}
                                                            <button type="button" class="btn btn-outline-secondary active" onclick={() => clearPreRemove()}>
                                                                <Icon inline icon="fa:trash-o" />
                                                            </button>
                                                            <button type="button" class="btn btn-danger" onclick={() => removeDelegate()}>
                                                                <Icon inline icon="fa:check" />
                                                            </button>
                                                        {:else}
                                                            <button type="button" class="btn btn-outline-secondary muted" onclick={() => setPreRemove(dir, dtype, item.id)}>
                                                                <Icon inline icon="fa:trash-o" />
                                                            </button>
                                                        {/if}
                                                    </div>
                                                {/if}
                                            </div>
                                        </div>
                                    {/each}
                                </div>
                            {/each}
                        {:else}
                            <div class="deleg-table-row deleg-table-row-empty">
                                <div>Aucun délégué</div>
                            </div>
                        {/if}
                    </div>
                {/each}

                <div class="deleg-add">
                    <div class="legend">Ajouter…</div>
                    <div class="legend">Délégué</div>
                    <div class="legend">Délégué de</div>
                    <div>
                        <div class="input-group input-group-sm">
                            <input type="text" class="form-control input-sm" placeholder="ID ou email d’un compte" bind:value={newDelegateText} />
                            <button type="button" class="btn btn-outline-secondary" disabled={working || preRemoveId != null || newDelegateText == ''} onclick={() => (newDelegateText = '')}><Icon inline icon="fa:times" /></button>
                        </div>
                    </div>
                    <div>
                        <div class="btn-group btn-group-sm">
                            <button type="button" class="btn btn-outline-secondary" disabled={newDelegateText.length < 5 || working || preRemoveId != null} onclick={() => addDelegate('direct', 'technical')}>Tech</button>
                            <button type="button" class="btn btn-outline-secondary" disabled={newDelegateText.length < 5 || working || preRemoveId != null} onclick={() => addDelegate('direct', 'renewal')}>Renew</button>
                        </div>
                    </div>
                    <div>
                        <div class="btn-group btn-group-sm">
                            <button type="button" class="btn btn-outline-secondary" disabled={$user.has_subaccounts > 0 || newDelegateText.length < 5 || working || preRemoveId != null} onclick={() => addDelegate('reverse', 'technical')}>Tech</button>
                            <button type="button" class="btn btn-outline-secondary" disabled={$user.has_subaccounts > 0 || newDelegateText.length < 5 || working || preRemoveId != null} onclick={() => addDelegate('reverse', 'renewal')}>Renew</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    {/if}
</div>
